import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { Calendar, Clock, MapPin, Users, Globe, Lock, Group, Share2, Edit, Trash2, Loader2, MessageCircle, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EventComments } from './comments/EventComments';
import { EventReminders } from './reminders/EventReminders';

interface Event {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  timezone: string;
  location: string;
  location_details: {
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  max_attendees: number | null;
  visibility: 'public' | 'private' | 'group';
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  cover_image: string;
  tags: string[];
  creator: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
  group?: {
    id: string;
    name: string;
    avatar_url: string;
  };
  attendees: Array<{
    user: {
      id: string;
      full_name: string;
      avatar_url: string;
    };
    status: 'yes' | 'no' | 'maybe';
  }>;
  user_rsvp?: {
    status: 'yes' | 'no' | 'maybe';
  }[];
  comments: Array<{
    id: string;
    user_id: string;
    content: string;
    parent_id: string | null;
    created_at: string;
    user: {
      id: string;
      full_name: string;
      avatar_url: string;
    };
  }>;
}

export function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [showAttendees, setShowAttendees] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'discussion' | 'reminders'>('details');

  useEffect(() => {
    loadEvent();
  }, [id]);

  const loadEvent = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          creator:profiles!creator_id(*),
          group:groups(*),
          attendees:event_attendees(
            user:profiles(*)
          ),
          user_rsvp:event_attendees(status),
          comments:event_comments(
            *,
            user:profiles(*)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setEvent(data);

      // Increment view count
      await supabase.rpc('increment_event_views', {
        event_uuid: id
      });
    } catch (error) {
      console.error('Error loading event:', error);
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (status: 'yes' | 'no' | 'maybe') => {
    if (!event) return;
    setRsvpLoading(true);

    try {
      const { error } = await supabase.rpc('rsvp_to_event', {
        event_uuid: event.id,
        rsvp_status: status
      });

      if (error) throw error;
      await loadEvent();
    } catch (error) {
      console.error('Error updating RSVP:', error);
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event || !window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', event.id);

      if (error) throw error;
      navigate('/events');
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error || 'Event not found'}</p>
      </div>
    );
  }

  const isCreator = event.creator.id === user?.id;
  const userRsvp = event.user_rsvp?.[0]?.status;
  const attendeesByStatus = {
    yes: event.attendees.filter(a => a.status === 'yes'),
    maybe: event.attendees.filter(a => a.status === 'maybe'),
    no: event.attendees.filter(a => a.status === 'no')
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Cover image */}
      <div className="relative h-64 rounded-xl overflow-hidden">
        <img
          src={event.cover_image || 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?auto=format&fit=crop&q=80'}
          alt={event.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Event header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">{event.title}</h1>
          <div className="mt-2 flex items-center gap-2 text-gray-600">
            {event.visibility === 'public' && <Globe className="w-4 h-4" />}
            {event.visibility === 'private' && <Lock className="w-4 h-4" />}
            {event.visibility === 'group' && <Group className="w-4 h-4" />}
            <span className="capitalize">{event.visibility} event</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isCreator && (
            <>
              <button
                onClick={() => navigate(`/events/${event.id}/edit`)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            onClick={() => {/* Implement share */}}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2",
              activeTab === 'details'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            <Calendar className="w-5 h-5" />
            Details
          </button>

          <button
            onClick={() => setActiveTab('discussion')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2",
              activeTab === 'discussion'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            <MessageCircle className="w-5 h-5" />
            Discussion
            {event.comments.length > 0 && (
              <span className="bg-royal-purple/10 text-royal-purple px-2 py-0.5 rounded-full text-xs">
                {event.comments.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('reminders')}
            className={cn(
              "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2",
              activeTab === 'reminders'
                ? "border-royal-purple text-royal-purple"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            )}
          >
            <Bell className="w-5 h-5" />
            Reminders
          </button>
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'details' && (
        <div className="grid gap-6 sm:grid-cols-[2fr,1fr]">
          <div className="space-y-6">
            {/* Description */}
            <div className="prose max-w-none">
              <p className="whitespace-pre-wrap">{event.description}</p>
            </div>

            {/* Tags */}
            {event.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-royal-purple/10 text-royal-purple rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Host info */}
            <div className="flex items-center gap-4">
              <img
                src={event.creator.avatar_url}
                alt={event.creator.full_name}
                className="w-12 h-12 rounded-full"
              />
              <div>
                <p className="text-sm text-gray-500">Hosted by</p>
                <p className="font-medium">{event.creator.full_name}</p>
              </div>
            </div>

            {/* Group info */}
            {event.group && (
              <div className="flex items-center gap-4">
                <img
                  src={event.group.avatar_url}
                  alt={event.group.name}
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <p className="text-sm text-gray-500">Group</p>
                  <p className="font-medium">{event.group.name}</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Date and time */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium">
                    {new Date(event.start_date).toLocaleDateString([], {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-sm text-gray-600">
                    {new Date(event.start_date).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    {' - '}
                    {new Date(event.end_date).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              {event.location && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium">{event.location}</p>
                    {event.location_details?.address && (
                      <p className="text-sm text-gray-600">
                        {[
                          event.location_details.address,
                          event.location_details.city,
                          event.location_details.state,
                          event.location_details.country
                        ].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* RSVP */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">RSVP</h3>
                {event.max_attendees && (
                  <p className="text-sm text-gray-600">
                    {attendeesByStatus.yes.length}/{event.max_attendees} spots filled
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                {(['yes', 'maybe', 'no'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => handleRSVP(status)}
                    disabled={rsvpLoading}
                    className={cn(
                      "flex-1 px-4 py-2 rounded-lg font-medium transition-colors",
                      userRsvp === status
                        ? "bg-royal-purple text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                  >
                    {status === 'yes' && 'Going'}
                    {status === 'maybe' && 'Maybe'}
                    {status === 'no' && 'Not Going'}
                  </button>
                ))}
              </div>
            </div>

            {/* Attendees */}
            <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
              <button
                onClick={() => setShowAttendees(!showAttendees)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  <h3 className="font-medium">
                    {attendeesByStatus.yes.length} attending
                  </h3>
                </div>
                <ChevronDown
                  className={cn(
                    "w-5 h-5 text-gray-600 transition-transform",
                    showAttendees && "rotate-180"
                  )}
                />
              </button>

              {showAttendees && (
                <div className="space-y-4">
                  {Object.entries(attendeesByStatus).map(([status, attendees]) => (
                    <div key={status}>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 capitalize">
                        {status === 'yes' && 'Going'}
                        {status === 'maybe' && 'Maybe'}
                        {status === 'no' && 'Not Going'}
                        {' '}
                        ({attendees.length})
                      </h4>
                      <div className="space-y-2">
                        {attendees.map((attendee) => (
                          <div
                            key={attendee.user.id}
                            className="flex items-center gap-3"
                          >
                            <img
                              src={attendee.user.avatar_url}
                              alt={attendee.user.full_name}
                              className="w-8 h-8 rounded-full"
                            />
                            <span className="text-sm">
                              {attendee.user.full_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'discussion' && (
        <EventComments
          eventId={event.id}
          comments={event.comments}
          onCommentUpdate={loadEvent}
        />
      )}

      {activeTab === 'reminders' && (
        <EventReminders
          eventId={event.id}
          startDate={event.start_date}
          title={event.title}
        />
      )}
    </div>
  );
}